import { inject, Injectable, signal } from '@angular/core';
import { HttpClient, HttpHeaders } from '@angular/common/http';
import type { HttpInterceptorFn } from '@angular/common/http';
import { firstValueFrom } from 'rxjs';
import { KIT_FIREBASE_AUTH } from '@rdlabo/ionic-angular-kit/auth-firebase';
import { environment } from '@rdlabo/shared/src/environments/environment';
import { customerCompanyHeader } from '@rdlabo/odss-mobile-hono/src/api/customer/context/api';
import type { CustomerCompany, CustomerContext } from '@rdlabo/odss-mobile-hono/src/api/customer/context/api';

const storageKey = 'customer.support-company';
interface SupportSession {
  uid: string;
  api: string;
  company: CustomerCompany;
}

@Injectable({ providedIn: 'root' })
export class CustomerCompanyService {
  readonly #auth = inject(KIT_FIREBASE_AUTH);
  readonly #http = inject(HttpClient);
  readonly #selection = signal<CustomerCompany | null>(null);
  readonly selection = this.#selection.asReadonly();

  /** Firebase の認証復元後に呼ぶ。別ユーザー・接続先の選択は引き継がない。 */
  restore(): CustomerCompany | null {
    const uid = this.#auth.currentUser?.uid;
    if (!uid) {
      return null;
    }
    const saved = sessionStorage.getItem(storageKey);
    const session: SupportSession | null = saved ? this.#parseSession(saved) : null;
    if (
      session?.uid === uid &&
      session.api === environment.api() &&
      Number.isSafeInteger(session.company?.companyId) &&
      session.company.companyId > 0 &&
      typeof session.company.companyName === 'string'
    ) {
      this.#selection.set(session.company);
      return session.company;
    }
    this.clear();
    return null;
  }

  #parseSession(value: string): SupportSession | null {
    // sessionStorage はユーザーが変更できるため、サーバー側でも毎回認可・存在確認する。
    try {
      return JSON.parse(value) as SupportSession;
    } catch {
      return null;
    }
  }

  getContext(): Promise<CustomerContext & { companies: CustomerCompany[] }> {
    return firstValueFrom(
      this.#http.get<CustomerContext & { companies: CustomerCompany[] }>(environment.api() + 'customer/context'),
    );
  }

  async switchCompany(companyId: number): Promise<void> {
    const uid = this.#auth.currentUser?.uid;
    if (!uid) {
      throw new Error('ログインしてください。');
    }
    const api = environment.api();
    const context = await firstValueFrom(
      this.#http.get<CustomerContext>(api + 'customer/context', {
        headers: new HttpHeaders({ [customerCompanyHeader]: String(companyId) }),
      }),
    );
    if (!context.supportCompany || this.#auth.currentUser?.uid !== uid || environment.api() !== api) {
      throw new Error('企業を切り替えられませんでした。');
    }
    sessionStorage.setItem(
      storageKey,
      JSON.stringify({ uid, api, company: context.supportCompany } satisfies SupportSession),
    );
    // Ionic のキャッシュ済みページ・進行中の通信も破棄する。
    window.location.assign('/main/search/transaction');
  }

  clear(): void {
    sessionStorage.removeItem(storageKey);
    this.#selection.set(null);
  }

  exit(): void {
    this.clear();
    window.location.assign('/main/settings');
  }
}

// Angular の interceptor / next は Observable 自体ではなく、Observable を返す関数。
// eslint-disable-next-line rxjs-x/finnish
export const customerCompanyInterceptor: HttpInterceptorFn = (request, next) => {
  const company = inject(CustomerCompanyService);
  const api = environment.api();
  const isCustomerRequest = request.url.startsWith(api + 'customer/') || request.url.startsWith(api + 'auth/');
  if (!isCustomerRequest || request.headers.has(customerCompanyHeader)) {
    return next(request);
  }
  const selection = company.restore();
  return next(
    selection ? request.clone({ setHeaders: { [customerCompanyHeader]: String(selection.companyId) } }) : request,
  );
};
